import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractAccountRowTemplate from "./AbstractAccountRowTemplate.js";
import GroupRow from "../../reports/rows/GroupRow.js";
import TranslatedString from "../../../TranslatedString.js";

/**
 * Composite template row.
 *
 * @param {row[]} [rows] - Sub rows
 * @param {TranslatedString} [title] - Title of the group
 * @param {boolean} [collapsed] - True if the group is collapsed in the report viewer
 */
export default class GroupRowTemplate extends AbstractAccountRowTemplate {
	constructor({ title, rows = [], collapsed = false } = {}) {
		super(...arguments);

		this._collapsed = collapsed;
		this._title = title;

		this.setRows(rows);
	}

	getTitle() {
		return this._title || "";
	}

	setTitle(title) {
		this._title = title;
	}

	getRows() {
		return this._rows;
	}

	setRows(rows) {
		this._rows = [];

		rows.forEach((row) => {
			row.addTo(this);
		});
	}

	setReportTemplate(template) {
		super.setReportTemplate(template);
		this._rows.forEach((row) => row.setReportTemplate(template));
	}

	setCollapsed(collapsed) {
		this._collapsed = collapsed;
	}

	isCollapsed() {
		return this._collapsed;
	}

	resolveReferences() {
		this._rows.forEach((row) => row.resolveReferences());
	}

	toggleCollapsed() {
		this._collapsed = !this._collapsed;
	}

	canHaveChildren() {
		return true;
	}

	canBeRepeated() {
		return this.getRows().every((row) => row.canBeRepeated());
	}

	canAdd(row) {
		return super.canAdd(...arguments) && row.canBeAddedToGroupRow(this);
	}

	addRow(row, sibling) {
		let position = this._rows.length;
		const siblingPosition = this._rowIndex(sibling);

		if (siblingPosition >= 0) {
			position = siblingPosition + 1;
		}

		row.setParent(this);
		this._rows.splice(position, 0, row);
		row.setReportTemplate(this._reportTemplate);
		this._collapsed = false;
	}

	removeRow(row) {
		row.setParent(null);
		this._rows = this._rows.filter((r) => r !== row);
	}

	accept(visitor) {
		return visitor.visitGroupRowTemplate(this);
	}

	_instanceClass() {
		return GroupRow;
	}

	_interpretationSpec(interpreter) {
		const spec = super._interpretationSpec(...arguments);

		spec.title = this._title;
		spec.collapsed = this._collapsed;
		spec.rows = interpreter.interpretItemTemplates(this._rows);

		return spec;
	}

	_rowIndex(row) {
		return this._rows.indexOf(row);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.children = this._rows;
		properties.title = this._title;

		return properties;
	}

	static getLabel() {
		return _("Group");
	}

	static getType() {
		return "GroupRowTemplate";
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(data, context);

		data.rows = context.factory.materializeAll(data.children, context);
		data.title = TranslatedString.materialize(data.title);
	}
}
